/**
 * Bumpable constant for forcing every client to wipe their `helix_*`
 * localStorage on next page load.
 *
 * The boot-check at `src/main.tsx:applyClientReset()` compares this value
 * to `localStorage['helix_reset_version']`. On mismatch:
 *   - every key starting with `helix_` is removed
 *   - the few non-prefixed keys we own (`chat_history`, `show_dev_action_logs`)
 *     are removed
 *   - `helix_reset_version` is rewritten to this new value
 *   - React mounts with empty client state
 *
 * BUMP THIS WHEN:
 *   - You wipe server-side player saves and need stale JWTs / cached
 *     character lists / saved characters cleared so they don't try to
 *     log into deleted records
 *   - A schema change makes existing cached state shape-incompatible
 *   - DJ explicitly says "give me a fresh PTR"
 *
 * Format: `YYYY-MM-DDTNNN` — the timestamp+counter suffix lets you bump
 * twice in a day if needed without ambiguity. Always increase
 * lexicographically.
 *
 * Initial value `2026-04-26T001` is the install seed: every client
 * already in the wild gets wiped exactly ONCE on next visit, then the
 * version sticks.
 */
export const CLIENT_RESET_VERSION = '2026-04-26T002';

/** localStorage key holding the last applied reset version. */
export const CLIENT_RESET_STORAGE_KEY = 'helix_reset_version';

/**
 * Non-prefixed localStorage keys owned by the H2000 client.
 * Wiped alongside `helix_*` keys on a reset bump.
 */
export const NON_PREFIXED_OWNED_KEYS = ['chat_history', 'show_dev_action_logs'] as const;

/**
 * Keys that must SURVIVE a client reset / version wipe — user-authored content
 * that should never be silently destroyed. Saved editor maps (`helix_level_*`,
 * including the `dev-map` Test workspace) + their index. This is what lets
 * /editor reset the SESSION (so it boots clean onto empty-canvas, not the
 * persisted character's last server map) without nuking the developer's maps.
 */
export const PRESERVE_PREFIXES = ['helix_level_'] as const;
export const PRESERVE_EXACT_KEYS = new Set<string>(['helix_levels_index']);

/**
 * Snapshot of the native `console.info` taken at module-load time.
 *
 * Why: `GameLogger` intercepts `console.info` from its constructor (which
 * runs as a side-effect of importing `src/utils/GameLogger.ts`). Any
 * `console.info(...)` call after that point routes into GameLogger's
 * rate-limited internal buffer and never reaches DevTools or Playwright's
 * CDP `Runtime.consoleAPICalled` event.
 *
 * This module is the FIRST import of `main.tsx` (and has no transitive
 * dep on GameLogger), so when this top-level statement runs we still see
 * the native `console.info`. We bind it here, then use the bound reference
 * inside the boot-check callback below — guaranteeing the [ClientReset]
 * message reaches both DJ's DevTools and any Playwright observer.
 */
const nativeConsoleInfo: ((...args: unknown[]) => void) | null =
  typeof console !== 'undefined' && typeof console.info === 'function'
    ? (console.info.bind(console) as (...args: unknown[]) => void)
    : null;

/**
 * Result of applying a client-reset check.
 *  - `noop` when the stored version already matches the constant
 *  - `installed` when no prior version was stored (first-time visit, silent)
 *  - `wiped` when an old version was upgraded — `removedKeys.length` keys were cleared
 */
export interface ApplyClientResetResult {
  outcome: 'noop' | 'installed' | 'wiped';
  removedKeys: string[];
  fromVersion: string | null;
  toVersion: string;
}

/**
 * Apply the client-reset check. Pure function — pass in the storage object
 * (real `localStorage` in the browser, a mock in tests) and the constant.
 *
 * On `outcome === 'wiped'` the caller should log; on 'installed' or 'noop',
 * stay silent. Caller is responsible for any console output / page reload.
 */
export function applyClientResetCheck(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>,
  version: string = CLIENT_RESET_VERSION,
): ApplyClientResetResult {
  const current = storage.getItem(CLIENT_RESET_STORAGE_KEY);
  if (current === version) {
    return { outcome: 'noop', removedKeys: [], fromVersion: current, toVersion: version };
  }

  const ownedNonPrefixed = new Set<string>(NON_PREFIXED_OWNED_KEYS);
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (!k) continue;
    if (k === CLIENT_RESET_STORAGE_KEY) continue;
    // Preserve user-authored maps (+ index) — never wipe editor work.
    if (PRESERVE_EXACT_KEYS.has(k)) continue;
    if (PRESERVE_PREFIXES.some((pre) => k.startsWith(pre))) continue;
    if (k.startsWith('helix_') || ownedNonPrefixed.has(k)) {
      toRemove.push(k);
    }
  }
  for (const k of toRemove) storage.removeItem(k);
  storage.setItem(CLIENT_RESET_STORAGE_KEY, version);

  return {
    outcome: current === null ? 'installed' : 'wiped',
    removedKeys: toRemove,
    fromVersion: current,
    toVersion: version,
  };
}

/**
 * Get the module-load-time native `console.info` reference.
 *
 * Returns `null` when console is unavailable (rare — non-browser host).
 * Callers should use this for boot-time logging that needs to bypass
 * any later console interception (e.g., GameLogger's intercept).
 */
export function getNativeConsoleInfo(): ((...args: unknown[]) => void) | null {
  return nativeConsoleInfo;
}
